import { useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import JWTContext from 'contexts/JWTContext';

/**
 * ImpersonationBanner - Shows when admin is impersonating a user.
 * Displays target user info and exit button.
 * Uses dual-condition check: isImpersonating OR user?.impersonated_by
 */
export default function ImpersonationBanner() {
    const { user, isImpersonating, impersonatedBy, exitImpersonation } = useContext(JWTContext);

    // Dual condition: state flag OR user object has impersonated_by
    const isActuallyImpersonating = isImpersonating || user?.impersonated_by;

    if (!isActuallyImpersonating) {
        return null;
    }

    const handleExit = async () => {
        try {
            await exitImpersonation();
        } catch (err) {
            console.error('Failed to exit impersonation:', err);
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 64, // Below AppBar
                left: 0,
                right: 0,
                zIndex: 1200,
                backgroundColor: 'warning.main',
                color: 'warning.contrastText',
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                boxShadow: 2
            }}
        >
            <WarningAmberIcon />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Impersonating: <strong>{user?.email}</strong>
                {user?.id && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                        ({user.id})
                    </Typography>
                )}
            </Typography>
            <Button
                variant="contained"
                size="small"
                color="inherit"
                startIcon={<ExitToAppIcon />}
                onClick={handleExit}
                sx={{
                    ml: 2,
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' }
                }}
            >
                Exit Impersonation
            </Button>
        </Box>
    );
}
